import type { PaletteMode } from '@mui/material/styles'
import type { TagPastel, TagPastelColors } from '@/theme/tag-pastels'
import {
  TAG_PASTELS,
  getTagPastels,
  tagPastelsLight,
} from '@/theme/tag-pastels'

/**
 * Couleurs de création / édition : les pastels du DS, dont on enregistre le fond (`Tag.color`).
 * Affichage : le hex de l'API est ramené à son pastel, qui donne fond, bordure et texte.
 */
export const TAG_COLORS: string[] = TAG_PASTELS.map(
  (pastel) => tagPastelsLight[pastel].bg
)

export const DEFAULT_TAG_COLOR = tagPastelsLight.ardoise.bg

const FALLBACK_PASTEL: TagPastel = 'perle'

/**
 * Couleurs enregistrées avant les pastels, ramenées à leur pastel. La migration du back les
 * réécrit en base ; on les garde ici pour les tags créés entre-temps et ceux de la console, qui
 * propose encore ses couleurs saturées.
 */
const PASTEL_BY_KNOWN_COLOR: Record<string, TagPastel> = {
  // Fonds de badge, palette de la webapp avant les pastels
  '#eef1f4': 'perle',
  '#e5ecf1': 'ardoise',
  '#dcf1ef': 'menthe',
  '#ece5f1': 'lavande',
  '#fdebd9': 'abricot',
  '#fddddd': 'corail',
  neutral: 'perle',
  blue: 'ardoise',
  green: 'menthe',
  violet: 'lavande',
  warn: 'abricot',
  danger: 'corail',
  // Ancienne palette saturée de la webapp
  '#ef4444': 'corail',
  '#f97316': 'abricot',
  '#eab308': 'miel',
  '#22c55e': 'sauge',
  '#14b8a6': 'menthe',
  '#3b82f6': 'ardoise',
  '#8b5cf6': 'lavande',
  '#ec4899': 'rose',
  '#6b7280': 'perle',
  '#000000': 'perle',
  // Palette de la console, et couleur par défaut du back
  '#06b6d4': 'lagon',
  '#64748b': 'perle',
  '#9e9e9e': 'perle',
}

const PASTEL_BY_BG = new Map<string, TagPastel>(
  TAG_PASTELS.map((pastel) => [
    tagPastelsLight[pastel].bg.toLowerCase(),
    pastel,
  ])
)

/** Normalise les couleurs API (hex sans #, hex court, espaces, casse). */
export const normalizeTagColorKey = (color: string): string => {
  const trimmed = color.trim().toLowerCase().replace(/^#/, '')
  if (/^[0-9a-f]{6}$/.test(trimmed)) return `#${trimmed}`
  if (/^[0-9a-f]{3}$/.test(trimmed)) {
    return `#${trimmed[0]}${trimmed[0]}${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}`
  }
  return trimmed
}

/** Teinte (0-360) et chroma (0-1) d'un hex. */
const hueAndChroma = (hex: string): { hue: number; chroma: number } | null => {
  if (!/^#[0-9a-f]{6}$/.test(hex)) return null
  const [r, g, b] = [1, 3, 5].map(
    (i) => parseInt(hex.slice(i, i + 2), 16) / 255
  )
  const max = Math.max(r, g, b)
  const chroma = max - Math.min(r, g, b)
  if (chroma === 0) return { hue: 0, chroma }
  const sector =
    max === r
      ? ((g - b) / chroma + 6) % 6
      : max === g
      ? (b - r) / chroma + 2
      : (r - g) / chroma + 4
  return { hue: sector * 60, chroma }
}

const PASTEL_HUES = TAG_PASTELS.filter(
  (pastel) => pastel !== FALLBACK_PASTEL
).map((pastel) => ({
  pastel,
  hue: hueAndChroma(tagPastelsLight[pastel].fg.toLowerCase())?.hue ?? 0,
}))

const hueDistance = (a: number, b: number): number => {
  const d = Math.abs(a - b) % 360
  return d > 180 ? 360 - d : d
}

/** Une couleur libre (hex saisi dans la console) va au pastel de teinte la plus proche. */
const nearestPastel = (hex: string): TagPastel => {
  const color = hueAndChroma(hex)
  if (!color || color.chroma < 0.1) return FALLBACK_PASTEL
  return PASTEL_HUES.reduce((best, candidate) =>
    hueDistance(candidate.hue, color.hue) < hueDistance(best.hue, color.hue)
      ? candidate
      : best
  ).pastel
}

export const getTagPastel = (color?: string | null): TagPastel => {
  if (!color?.trim()) return FALLBACK_PASTEL
  const key = normalizeTagColorKey(color)
  return (
    PASTEL_BY_BG.get(key) ?? PASTEL_BY_KNOWN_COLOR[key] ?? nearestPastel(key)
  )
}

export const getTagPastelColors = (
  color?: string | null,
  mode: PaletteMode = 'light'
): TagPastelColors => getTagPastels(mode)[getTagPastel(color)]

/** La pastille d'un tag dans une liste : la couleur de son texte, lisible sur la surface. */
export const getTagDotColor = (
  color?: string | null,
  mode: PaletteMode = 'light'
): string => getTagPastelColors(color, mode).fg

/** Style d'un chip : le trio du pastel, et la pastille du DS devant le libellé. */
export const getTagChipSx = (
  color?: string | null,
  mode: PaletteMode = 'light'
) => {
  const { bg, border, fg } = getTagPastelColors(color, mode)
  return {
    bgcolor: bg,
    backgroundColor: bg,
    color: fg,
    border: `1px solid ${border}`,
    fontSize: 10,
    fontWeight: 500,
    height: 22,
    borderRadius: '999px',
    '&.MuiChip-filled, &.MuiChip-filledDefault': {
      backgroundColor: bg,
      color: fg,
    },
    '& .MuiChip-label::before': {
      content: '""',
      display: 'inline-block',
      width: 6,
      height: 6,
      borderRadius: '50%',
      backgroundColor: fg,
      marginRight: '4px',
      verticalAlign: 'middle',
      position: 'relative',
      top: '-1px',
    },
    '& .MuiChip-deleteIcon': {
      color: fg,
      opacity: 0.65,
      '&:hover': {
        color: fg,
        opacity: 1,
      },
    },
  }
}
